import { Button, Icon, LinearProgress, MuiAlert, Stack, TextField, Typography } from '@fox/atoms';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useComponentClassContext } from '../component-class-context';
import { useComponentStyleContext } from '../component-style-context';
import { useContentContext } from '../content-context';
import { reloadOnAccessLost } from '../Membership/protected-page-reload';
import { SiteSearchResultsModel } from './contracts';
import { RenderSearchResults } from './search-results';
import { SearchBackdropStyles, SearchModal, SearchTrigger } from './styled';

const submitSearch = async (searchValue: string, contentId: string) => {
	const resp = await fetch(`/Umbraco/Api/SiteSearch/SearchSite?searchValue=${encodeURIComponent(searchValue)}&contentId=${contentId}`, {
		method: 'GET',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json'
		}
	});
	if (reloadOnAccessLost(resp.status)) {
		return undefined;
	}
	return resp.json();
};

export const SiteSearch = () => {
	const { contentId } = useContentContext();
	const [searchValue, setSearchValue] = useState<string>('');
	const [searchResults, setSearchResults] = useState<SiteSearchResultsModel | undefined>(undefined);
	const [submitDisabled, setSubmitDisabled] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<boolean>(false);
	const { fontSize, fontColour, headerFont, headerFontColour, font, fontTextShadow, headerTextShadow } = useComponentStyleContext();
	const { headerClass } = useComponentClassContext();

	const handleSubmit = async () => {
		setSubmitDisabled(true);
		const results = await submitSearch(searchValue, contentId);
		if (results?.results) {
			setSearchResults(results);
			if (error) {
				setError(false);
			}
		} else {
			setError(true);
		}
		setSubmitDisabled(false);
	};

	return (
		<>
			<SearchTrigger onClick={() => setShowModal(true)}>
				<Icon iconName="Search" sx={{ ...fontSize, ...fontColour, ...fontTextShadow }} />
			</SearchTrigger>
			{showModal ? <SearchBackdropStyles /> : null}
			<SearchModal show={showModal} onHide={() => setShowModal(false)} backdropClassName="site-search-backdrop" centered>
				<Modal.Header>
					<Typography variant="h5" className={headerClass} style={{ ...headerTextShadow, ...headerFont, ...headerFontColour }}>
						Search
					</Typography>
					<button type="button" className="close" aria-label="Close" onClick={() => setShowModal(false)}>
						<span aria-hidden="true">×</span>
					</button>
				</Modal.Header>
				<Stack width="100%" gap={2} p={2} style={{ ...font, ...fontColour }}>
					<Stack direction="row" alignItems="center" gap={1}>
						<TextField
							fullWidth
							onChange={e => setSearchValue(e.target.value)}
							placeholder="Search this site"
							onKeyDown={async e => {
								if ((e.key === 'Enter' || e.keyCode === 13) && searchValue !== '') {
									handleSubmit();
								}
							}}
						/>
						<Button
							variant="primary"
							disabled={submitDisabled}
							onClick={async () => {
								if (searchValue !== '') {
									handleSubmit();
								}
							}}
						>
							Go
						</Button>
					</Stack>
					{submitDisabled ? <LinearProgress aria-label="Searching site" /> : null}
					<RenderSearchResults results={searchResults} />
					{error ? <MuiAlert severity="error">Error: Something went wrong with your search.</MuiAlert> : null}
				</Stack>
			</SearchModal>
		</>
	);
};
